/**
 * Ghar Mart Prestige Theme - Global Animation Controller
 * Manages scroll reveal observer, staggered entrance delays,
 * interactive micro-interactions, and conversion animations across the entire store.
 */

(function() {
  'use strict';

  // Safeguard: Check if animations are supported
  if (typeof window === 'undefined') return;

  const GharMartAnimations = {
    observer: null,
    staggerObserver: null,

    init() {
      // Don't animate if user prefers reduced motion
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        document.documentElement.classList.add('reduced-motion');
        return;
      }

      this.setupIntersectionObserver();
      this.autoTagElements();
      this.setupStaggerIndices();
      this.observeElements();
      this.initMicroInteractions();
      this.listenForDynamicContent();
      this.initCartBadgeWatchers();

      // Trigger custom event
      document.dispatchEvent(new CustomEvent('gharmart:animations:ready'));
    },

    /**
     * Create the primary IntersectionObserver for scroll reveals
     */
    setupIntersectionObserver() {
      if (!('IntersectionObserver' in window)) {
        // Fallback: Reveal everything immediately for legacy browsers
        document.querySelectorAll('[data-animate], .reveal-on-scroll, .animate-on-scroll').forEach(el => {
          el.classList.add('is-visible', 'in-viewport');
        });
        return;
      }

      const options = {
        root: null,
        rootMargin: '0px 0px -40px 0px',
        threshold: 0.08
      };

      this.observer = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const target = entry.target;
            target.classList.add('is-visible', 'in-viewport');

            // If it's a grid/container, reveal children with stagger
            if (target.matches('.featured-products__grid, .trust-guarantees__grid, .testimonials-grid, .products-grid, .product-recommendations__grid, .category-pills__marquee-track, .anim-stagger')) {
              const children = target.children;
              Array.from(children).forEach((child, index) => {
                child.style.setProperty('--anim-index', index);
                setTimeout(() => {
                  child.classList.add('is-visible', 'in-viewport');
                }, index * 75);
              });
            }

            // Once revealed, unobserve to free memory
            observer.unobserve(target);
          }
        });
      }, options);
    },

    /**
     * Automatically tags key components across the website
     * so every section, card, and title gets animated effortlessly.
     */
    autoTagElements() {
      // 1. Section headers & titles
      const headers = document.querySelectorAll(
        '.section__header, .section__heading, .section__subheading, .category-pills__header, .trust-guarantees .section__header, .testimonials .section__header'
      );
      headers.forEach(el => {
        if (!el.hasAttribute('data-animate') && !el.classList.contains('reveal-on-scroll')) {
          el.setAttribute('data-animate', '');
          el.classList.add('reveal-on-scroll', 'anim-fade-up');
        }
      });

      // 2. Grids and list containers
      const grids = document.querySelectorAll(
        '.featured-products__grid, .trust-guarantees__grid, .testimonials-grid, .collection-grid, .products-grid, .product-recommendations__grid, .footer__columns, .about-values-grid, .about-stats-grid, .contact-cards-grid'
      );
      grids.forEach(el => {
        if (!el.hasAttribute('data-animate')) {
          el.setAttribute('data-animate', '');
          el.classList.add('reveal-on-scroll', 'anim-stagger');
        }
      });

      // 3. Product Cards
      const productCards = document.querySelectorAll('.product-card, .card--product');
      productCards.forEach((card, index) => {
        if (!card.hasAttribute('data-animate')) {
          card.setAttribute('data-animate', '');
          card.classList.add('reveal-on-scroll', 'anim-fade-up');
        }
      });

      // 4. Trust guarantee items
      const trustCards = document.querySelectorAll('.trust-card, .trust-guarantees__item');
      trustCards.forEach((card, index) => {
        if (!card.hasAttribute('data-animate')) {
          card.setAttribute('data-animate', '');
          card.classList.add('reveal-on-scroll', 'anim-fade-up');
        }
      });

      // 5. Testimonial items
      const testimonialCards = document.querySelectorAll('.testimonial-card, .testimonials-card');
      testimonialCards.forEach((card, index) => {
        if (!card.hasAttribute('data-animate')) {
          card.setAttribute('data-animate', '');
          card.classList.add('reveal-on-scroll', 'anim-fade-up');
        }
      });

      // 6. Category pills
      const pillsContainer = document.querySelector('.category-pills__marquee-container');
      if (pillsContainer && !pillsContainer.hasAttribute('data-animate')) {
        pillsContainer.setAttribute('data-animate', '');
        pillsContainer.classList.add('reveal-on-scroll', 'anim-fade-up');
      }

      // 7. Footer sections
      const footerTop = document.querySelector('.footer__top');
      if (footerTop && !footerTop.hasAttribute('data-animate')) {
        footerTop.setAttribute('data-animate', '');
        footerTop.classList.add('reveal-on-scroll', 'anim-fade-up');
      }

      const footerTrust = document.querySelector('.footer__trust-bar');
      if (footerTrust && !footerTrust.hasAttribute('data-animate')) {
        footerTrust.setAttribute('data-animate', '');
        footerTrust.classList.add('reveal-on-scroll', 'anim-fade-up');
      }
    },

    /**
     * Compute and assign stagger indices to sibling cards
     */
    setupStaggerIndices() {
      const parentSelectors = [
        '.featured-products__grid',
        '.trust-guarantees__grid',
        '.testimonials-grid',
        '.collection__grid',
        '.products-grid',
        '.product-recommendations__grid',
        '.footer__columns',
        '.about-values-grid',
        '.about-stats-grid',
        '.contact-cards-grid'
      ];

      parentSelectors.forEach(selector => {
        const parents = document.querySelectorAll(selector);
        parents.forEach(parent => {
          Array.from(parent.children).forEach((child, index) => {
            child.style.setProperty('--anim-index', index % 4);
          });
        });
      });
    },

    /**
     * Observe all elements and immediately reveal above-the-fold elements
     */
    observeElements() {
      if (!this.observer) return;

      const elements = document.querySelectorAll('[data-animate], .reveal-on-scroll, .animate-on-scroll');
      const windowHeight = window.innerHeight || document.documentElement.clientHeight;

      elements.forEach(el => {
        const rect = el.getBoundingClientRect();

        // If element is already in the viewport on page load, reveal it with a graceful mini-stagger
        if (rect.top < windowHeight && rect.bottom > 0) {
          setTimeout(() => {
            el.classList.add('is-visible', 'in-viewport');
          }, 80);
        } else {
          this.observer.observe(el);
        }
      });
    },

    /**
     * Interactive Micro-Interactions (Wishlist bounce, button ripple, etc.)
     */
    initMicroInteractions() {
      // 1. Wishlist Heart bounce micro-interaction
      document.addEventListener('click', (e) => {
        const wishlistBtn = e.target.closest('[data-wishlist-toggle], .product-card__wishlist');
        if (wishlistBtn) {
          wishlistBtn.classList.remove('is-animating');
          // Force reflow
          void wishlistBtn.offsetWidth;
          wishlistBtn.classList.add('is-animating');
          setTimeout(() => {
            wishlistBtn.classList.remove('is-animating');
          }, 700);
        }
      });

      // 2. Button press elastic feedback
      document.addEventListener('mousedown', (e) => {
        const btn = e.target.closest('.button, .btn-card-quickbuy, .btn-whatsapp-order, .sticky-buy-btn');
        if (btn) {
          btn.style.transform = 'scale(0.96)';
        }
      });

      document.addEventListener('mouseup', (e) => {
        const btn = e.target.closest('.button, .btn-card-quickbuy, .btn-whatsapp-order, .sticky-buy-btn');
        if (btn) {
          btn.style.transform = '';
        }
      });

      // 3. Product thumbnail click smooth transition
      document.addEventListener('click', (e) => {
        const thumb = e.target.closest('.gallery-thumb');
        if (thumb) {
          const mainImg = document.querySelector('.product-gallery__main-image');
          if (mainImg) {
            mainImg.style.opacity = '0.3';
            mainImg.style.transform = 'scale(0.98)';
            setTimeout(() => {
              mainImg.style.opacity = '1';
              mainImg.style.transform = 'scale(1)';
            }, 180);
          }
        }
      });
    },

    /**
     * Watch cart count changes and animate header cart badge
     */
    initCartBadgeWatchers() {
      const triggerBadgeBounce = () => {
        const badges = document.querySelectorAll('.cart-count-badge, .header__cart-count, .cart-drawer__count-badge');
        badges.forEach(badge => {
          badge.classList.remove('is-bouncing');
          void badge.offsetWidth;
          badge.classList.add('is-bouncing');
          setTimeout(() => {
            badge.classList.remove('is-bouncing');
          }, 650);
        });
      };

      // Expose globally
      window.GharMartAnimateCartBadge = triggerBadgeBounce;

      // Listen for cart changes
      document.addEventListener('cart:updated', triggerBadgeBounce);
      document.addEventListener('cart:item-added', triggerBadgeBounce);

      // Intercept fetch calls to Shopify cart API
      const originalFetch = window.fetch;
      if (originalFetch) {
        window.fetch = function(...args) {
          const url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url ? args[0].url : '');
          return originalFetch.apply(window, args).then(response => {
            if (typeof url === 'string' && (url.includes('/cart/add') || url.includes('/cart/change') || url.includes('/cart/clear') || url.includes('/cart/update'))) {
              setTimeout(triggerBadgeBounce, 300);
            }
            return response;
          });
        };
      }
    },

    /**
     * Listen for dynamic content (Shopify theme customizer, pagination, modal popups)
     */
    listenForDynamicContent() {
      // Re-observe when Shopify Theme Editor loads or updates sections
      document.addEventListener('shopify:section:load', () => {
        this.autoTagElements();
        this.setupStaggerIndices();
        this.observeElements();
      });

      // Observe DOM mutations for newly added product cards (e.g., infinite scroll or filtering)
      const observer = new MutationObserver((mutations) => {
        let hasNewNodes = false;
        mutations.forEach(mutation => {
          if (mutation.addedNodes && mutation.addedNodes.length > 0) {
            mutation.addedNodes.forEach(node => {
              if (node.nodeType === 1 && (node.matches?.('.product-card') || node.querySelector?.('.product-card'))) {
                hasNewNodes = true;
              }
            });
          }
        });

        if (hasNewNodes) {
          this.autoTagElements();
          this.setupStaggerIndices();
          this.observeElements();
        }
      });

      const mainContent = document.getElementById('MainContent') || document.body;
      if (mainContent) {
        observer.observe(mainContent, { childList: true, subtree: true });
      }
    }
  };

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => GharMartAnimations.init());
  } else {
    GharMartAnimations.init();
  }

  // Expose globally
  window.GharMartAnimations = GharMartAnimations;
})();
